/**
 * BrochureManager — lists every brochure attached to a Hotel / Query / Tour
 * and lets the operator create as many as they want:
 *   • "Brochure"        — pre-trip branded showcase (existing concept)
 *   • "Memories Album"  — post-tour photo/video collection for the guests
 * Selecting one opens the Brochure Studio for that exact brochure.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Camera, Copy, Eye, Image as ImageIcon, Loader2, Pencil, Plus, Sparkles, Trash2,
} from 'lucide-react';
import BrochureStudio from './BrochureStudio';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_SHARE_BASE = (process.env.REACT_APP_PUBLIC_SHARE_DOMAIN || 'https://pollendots.com').replace(/\/$/, '');

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function BrochureManager({
  open, onOpenChange, ownerKind, ownerId, defaultTitle = '',
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState('');
  const [studioId, setStudioId] = useState('');
  const [studioOpen, setStudioOpen] = useState(false);
  const [viewsFor, setViewsFor] = useState(null);
  const [viewsData, setViewsData] = useState(null);

  const openViews = async (b) => {
    setViewsFor(b); setViewsData(null);
    try {
      const r = await fetch(`${API}/api/pollen/brochures/${b.id}/views`, { credentials: 'include', headers: authHeaders() });
      setViewsData(await r.json());
    } catch { setViewsData({ views: [], daily: [] }); }
  };
  const device = (ua = '') => (/whatsapp/i.test(ua) ? 'WhatsApp' : /iphone|ipad/i.test(ua) ? 'iPhone' : /android/i.test(ua) ? 'Android' : /windows/i.test(ua) ? 'Windows' : /mac/i.test(ua) ? 'Mac' : 'Other');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/pollen/brochures/by-owner/${ownerKind}/${ownerId}`,
        { credentials: 'include', headers: authHeaders() }).then(r => r.json());
      setItems(r.brochures || (r.brochure ? [r.brochure] : []));
    } catch { toast.error('Could not load brochures.'); }
    finally { setLoading(false); }
  }, [ownerKind, ownerId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const createNew = async (kind) => {
    setCreating(kind);
    try {
      const r = await fetch(`${API}/api/pollen/brochures`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          owner_kind: ownerKind, owner_id: ownerId, kind, force_new: true,
          title: kind === 'memories'
            ? (defaultTitle ? `${defaultTitle} — Memories` : '')
            : (defaultTitle || ''),
        }),
      });
      if (!r.ok) throw new Error();
      const doc = await r.json();
      setItems(prev => [doc, ...prev]);
      setStudioId(doc.id);
      setStudioOpen(true);
    } catch { toast.error('Could not create.'); }
    finally { setCreating(''); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete “${b.title}” permanently? Its share URL stops working immediately.`)) return;
    try {
      const r = await fetch(`${API}/api/pollen/brochures/${b.id}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders(),
      });
      if (!r.ok) throw new Error();
      setItems(prev => prev.filter(x => x.id !== b.id));
      toast.success('Deleted');
    } catch { toast.error('Could not delete.'); }
  };

  const copyLink = async (b) => {
    const url = `${PUBLIC_SHARE_BASE}/api/og/brochure/${b.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied');
    } catch {
      toast.error('Could not copy — long-press the link to copy manually.');
    }
  };

  return (
    <>
      <Sheet open={open && !studioOpen} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="h-[85vh] overflow-y-auto" data-testid="brochure-manager">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-700" />
              <span className="font-serif" style={{ letterSpacing: '0.1em' }}>Brochures</span>
            </SheetTitle>
            <SheetDescription className="text-xs text-muted-foreground">
              Create as many as you need — a pre-trip brochure to excite, a post-tour memories album to relive.
            </SheetDescription>
          </SheetHeader>

          <div className="grid grid-cols-2 gap-2 pt-4">
            <Button variant="outline" disabled={!!creating}
                    className="h-14 flex-col gap-0.5 border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
                    onClick={() => createNew('brochure')} data-testid="new-brochure-btn">
              {creating === 'brochure' ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-1 text-sm font-medium"><Plus className="w-3.5 h-3.5" /><Sparkles className="w-3.5 h-3.5" />New Brochure</span>}
              <span className="text-[10px] font-normal opacity-70">Pre-trip branded showcase</span>
            </Button>
            <Button variant="outline" disabled={!!creating}
                    className="h-14 flex-col gap-0.5 border-rose-300 bg-rose-50 text-rose-900 hover:bg-rose-100"
                    onClick={() => createNew('memories')} data-testid="new-memories-btn">
              {creating === 'memories' ? <Loader2 className="w-4 h-4 animate-spin" /> : <span className="flex items-center gap-1 text-sm font-medium"><Plus className="w-3.5 h-3.5" /><Camera className="w-3.5 h-3.5" />Memories Album</span>}
              <span className="text-[10px] font-normal opacity-70">Post-tour photos & videos for guests</span>
            </Button>
          </div>

          <div className="pt-4 space-y-2 pb-10">
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-amber-700" /></div>
            ) : items.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">
                Nothing yet — create your first brochure or memories album above.
              </p>
            ) : items.map(b => {
              const isMem = b.kind === 'memories';
              return (
                <div key={b.id}
                     className={`rounded-lg border p-3 ${isMem ? 'border-rose-200 bg-rose-50/40' : 'border-amber-200 bg-amber-50/40'}`}
                     data-testid={`brochure-row-${b.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <button className="flex-1 min-w-0 text-left" onClick={() => { setStudioId(b.id); setStudioOpen(true); }}
                            data-testid={`brochure-open-${b.id}`}>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {isMem ? <Camera className="w-3.5 h-3.5 text-rose-600 shrink-0" /> : <Sparkles className="w-3.5 h-3.5 text-amber-700 shrink-0" />}
                        <span className="font-medium text-sm truncate">{b.title || 'Untitled'}</span>
                        <Badge variant="outline" className={`text-[9px] ${isMem ? 'border-rose-300 text-rose-700' : 'border-amber-300 text-amber-800'}`}>
                          {isMem ? 'Memories' : 'Brochure'}
                        </Badge>
                        <Badge variant="outline" className={`text-[9px] ${b.is_published ? 'border-emerald-300 text-emerald-700' : 'border-slate-300 text-slate-500'}`}>
                          {b.is_published ? 'Live' : 'Draft'}
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2.5">
                        <span className="flex items-center gap-0.5"><ImageIcon className="w-3 h-3" />{(b.gallery || []).length} items</span>
                        <span role="button" tabIndex={0} className="flex items-center gap-0.5 underline decoration-dotted cursor-pointer text-amber-900"
                              onClick={(e) => { e.stopPropagation(); openViews(b); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openViews(b); } }}
                              data-testid={`brochure-views-${b.id}`}>
                          <Eye className="w-3 h-3" />{b.view_count || 0} views
                        </span>
                        {b.last_viewed_at && <span>seen {b.last_viewed_at.slice(0, 10)}</span>}
                        <span>{(b.created_at || '').slice(0, 10)}</span>
                      </p>
                    </button>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Edit in studio"
                              onClick={() => { setStudioId(b.id); setStudioOpen(true); }} data-testid={`brochure-edit-${b.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      {b.is_published && (
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="Copy share link"
                                onClick={() => copyLink(b)} data-testid={`brochure-copy-${b.id}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600" title="Delete"
                              onClick={() => remove(b)} data-testid={`brochure-delete-${b.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={!!viewsFor} onOpenChange={(o) => { if (!o) setViewsFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="brochure-views-dialog">
          <DialogHeader><DialogTitle className="text-sm flex items-center gap-1.5">
            <Eye className="w-4 h-4 text-amber-700" />{viewsFor?.title || 'Views'} — {viewsFor?.view_count || 0} total views
          </DialogTitle></DialogHeader>
          {!viewsData ? (
            <div className="py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-amber-700" /></div>
          ) : (
            <div className="space-y-3">
              {viewsData.daily?.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold mb-1">Views per day</p>
                  <div className="space-y-0.5">
                    {viewsData.daily.slice(0, 7).map((d) => (
                      <div key={d.date} className="flex items-center gap-2 text-[11px]">
                        <span className="w-20 text-muted-foreground">{d.date}</span>
                        <div className="h-2 bg-amber-400 rounded" style={{ width: `${Math.min(d.n * 14, 160)}px` }} />
                        <span className="font-medium">{d.n}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="text-[11px] font-semibold mb-1">Recent views</p>
                {(viewsData.views || []).length === 0 ? (
                  <p className="text-[11px] text-muted-foreground">No tracked views yet — detailed tracking starts now; the total above includes earlier views.</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {viewsData.views.slice(0, 30).map((v) => (
                      <div key={v.id} className="flex items-center justify-between text-[11px] border-b border-dashed pb-0.5">
                        <span>{(v.ts || '').slice(0, 16).replace('T', ' ')}</span>
                        <span className="text-muted-foreground">{device(v.ua)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {studioId && (
        <BrochureStudio
          open={studioOpen}
          onOpenChange={(o) => { setStudioOpen(o); if (!o) load(); }}
          ownerKind={ownerKind}
          ownerId={ownerId}
          brochureId={studioId}
          defaultTitle={defaultTitle}
        />
      )}
    </>
  );
}
